import { formUrl } from "./config";
import { captchaToken, captchaEnabled } from "./captcha";

/**
 * Shared submission layer for every public-facing form.
 *
 * All submissions go to OUR OWN backend endpoints — the Node API under
 * `/api/forms/*` by default (configurable through VITE_PUBLIC_FORM_BASE_URL).
 * The handlers answer with the `{ code, data, meta }` envelope and return
 * { code: "OK" } on success. Client-side we mirror the server rules: honeypot
 * fields are read and dropped before any network call, and an optional captcha
 * token is attached when configured.
 */

export const FORM_ENDPOINTS = {
  contact: "/api/forms/contact",
  newsletter: "/api/forms/newsletter",
  listBusiness: "/api/forms/list-business",
  businessEnquiry: "/api/forms/business-enquiry",
} as const;

export type FormEndpoint = (typeof FORM_ENDPOINTS)[keyof typeof FORM_ENDPOINTS];

export interface FormSubmitResult {
  ok: boolean;
  message: string;
}

// Reserved honeypot field names (kept legitimate-looking so AutoFill ignores them).
const HONEYPOT_FIELDS = ["website_alt", "company_alt", "phone_alt", "mobile_alt", "contact_alt"];

interface Envelope {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

/**
 * Reads and strips the honeypot fields. Returns true when the submission looks
 * like a bot (any honeypot filled), in which case the caller shows the normal
 * success state and sends nothing — identical to a real submission.
 */
function stripHoneypots(data: FormData): boolean {
  let tripped = false;
  for (const field of HONEYPOT_FIELDS) {
    const value = String(data.get(field) ?? "").trim();
    if (value !== "") tripped = true;
    if (data.has(field)) data.delete(field);
  }
  return tripped;
}

function toBody(data: FormData): URLSearchParams {
  const body = new URLSearchParams();
  data.forEach((value, key) => {
    if (typeof value === "string") body.append(key, value);
  });
  return body;
}

function serverMessage(parsed: Envelope | null, fallback: string): string {
  if (!parsed) return fallback;
  const candidate = parsed.meta?.message || parsed.message || parsed.meta?.detail;
  return typeof candidate === "string" && candidate.trim() ? candidate : fallback;
}

/**
 * Submits a form to our backend and returns a normalised result. Never throws:
 * the caller only has to render `ok` / `message`.
 */
export async function submitForm(
  endpoint: FormEndpoint | string,
  data: FormData,
  successMessage: string,
  options: { captchaAction?: string } = {},
): Promise<FormSubmitResult> {
  // Honeypot trip → pretend it worked, send nothing.
  if (stripHoneypots(data)) {
    return { ok: true, message: successMessage };
  }

  // Optional captcha token (invisible for reCAPTCHA v3, widget token for Turnstile).
  if (captchaEnabled()) {
    const token = await captchaToken(options.captchaAction ?? "submit");
    if (token) data.set("captcha_token", token);
  }

  try {
    const res = await fetch(formUrl(endpoint), {
      method: "POST",
      headers: { Accept: "application/json" },
      body: toBody(data),
    });

    const responseText = await res.text();
    let parsed: Envelope | null = null;
    if (responseText) {
      try {
        parsed = JSON.parse(responseText) as Envelope;
      } catch {
        parsed = null;
      }
    }

    const code = parsed?.code;
    const message = serverMessage(parsed, responseText);
    const isSpam = typeof message === "string" && message.toLowerCase().includes("spam");

    if (res.ok && code === "OK" && !isSpam) {
      return { ok: true, message: successMessage };
    }
    return { ok: false, message: message || "We couldn't send your message. Please try again." };
  } catch {
    return { ok: false, message: "Network error. Please check your connection and try again." };
  }
}